import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatOwnershipTransfersRepo} from '../../repository/NatOwnershipTransfersRepo'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import EventEmitterExGuest from '../../../../../components/shared/pageBaseComponents/EventEmitterExGuest'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import moment from 'moment'
import {Messages} from '../../../../../Messages'
import {RequestItemError} from '../../../../../network/response/RequestItemError'
import {Nullable} from '../../../../../app/TypeHelpers'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {HKConstants} from '../../../../../HKConstants'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import styled from 'styled-components'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import {DefaultStyledButton} from '../../../../../styledComponents/Button'
import {
    GuestApplyButton,
    GuestBackButton,
    ItemHeading,
    ListGridContainer,
    WideWhiteArea,
} from '../../common/NatOwnershipTransfersCommon'
import {NatZipCode} from '../../../condominium/repository/NatZipCode'
import NatGuestOwnershipTransfersDetailFormItem from '../../common/NatGuestOwnershipTransfersDetailFormItem'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import {NatOwnershipTransferOldMemberLocationState} from '../oldMember/NatGuestOldMemberInterface'
import {Colors, FontSize, FontWeights} from '../../../../../styledComponents/Constants'
import {DisplayItemInputTitleNoMargin} from '../../../../../styledComponents/DisplayItem'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper,
} from '../../../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import {NatPostOldMemberRequest} from '../../repository/NatPostOldMemberRequest'
import NatResidentApplicationDetailFormItem
    from '../../../applications/common/resident/NatResidentApplicationDetailFormItem'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {Location, NavigateFunction} from 'react-router-dom'


interface Props {
    condominiumCd: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatOwnershipTransferOldMemberLocationState>
    eventEmitter: EventEmitterExGuest
    asyncCallFunction: AsyncCallFunctionType

    familyName: Nullable<string>
    firstName: Nullable<string>
    moveOutDate: Nullable<string>
    phoneNumberAfterMoveOut: Nullable<string>
    zipCodeAfterMoveOut: Nullable<string>
    prefectureAfterMoveOut: Nullable<string>
    address2AfterMoveOut: Nullable<string>
    address3AfterMoveOut: Nullable<string>
    buildingRoomNumberAfterMoveOut: Nullable<string>
    mailAddress: Nullable<string>
    mailAddressConfirm: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPostOldMemberRequest) => Promise<void>
    onClickBackButton: () => void
}

interface State {
    familyName: string
    firstName: string
    moveOutDate: string
    phoneNumberAfterMoveOut: string
    zipCodeAfterMoveOut: string
    prefectureAfterMoveOut: string
    address2AfterMoveOut: string
    address3AfterMoveOut: string
    buildingRoomNumberAfterMoveOut: string
    mailAddress: string
    mailAddressConfirm: string

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatGuestOldMemberForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            familyName: '',
            firstName: '',
            moveOutDate: '',
            phoneNumberAfterMoveOut: '',
            zipCodeAfterMoveOut: '',
            prefectureAfterMoveOut: '',
            address2AfterMoveOut: '',
            address3AfterMoveOut: '',
            buildingRoomNumberAfterMoveOut: '',
            mailAddress: '',
            mailAddressConfirm: '',
            currentDate: moment(),
            holidays: [],
            applyButtonDisabled: true,
            isLoadCompleted: false,
        }

        this.handleChangeMoveOutDate = this.handleChangeMoveOutDate.bind(this)
        this.handleChangeText = this.handleChangeText.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            this.setState({
                familyName: this.props.familyName ?? this.state.familyName,
                firstName: this.props.firstName ?? this.state.firstName,
                moveOutDate: this.props.moveOutDate ?? this.state.moveOutDate,
                phoneNumberAfterMoveOut: this.props.phoneNumberAfterMoveOut ?? this.state.phoneNumberAfterMoveOut,
                zipCodeAfterMoveOut: this.props.zipCodeAfterMoveOut ?? this.state.zipCodeAfterMoveOut,
                prefectureAfterMoveOut: this.props.prefectureAfterMoveOut ?? this.state.prefectureAfterMoveOut,
                address2AfterMoveOut: this.props.address2AfterMoveOut ?? this.state.address2AfterMoveOut,
                address3AfterMoveOut: this.props.address3AfterMoveOut ?? this.state.address3AfterMoveOut,
                buildingRoomNumberAfterMoveOut:
                    this.props.buildingRoomNumberAfterMoveOut ?? this.state.buildingRoomNumberAfterMoveOut,
                mailAddress: this.props.mailAddress ?? this.state.mailAddress,
                mailAddressConfirm: this.props.mailAddressConfirm ?? this.state.mailAddressConfirm,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeMoveOutDate(date: string): void {
        this.setState({moveOutDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            familyName, firstName, moveOutDate, zipCodeAfterMoveOut,
            phoneNumberAfterMoveOut, prefectureAfterMoveOut,
            address2AfterMoveOut, address3AfterMoveOut,
        } = this.state

        let applyButtonDisabled = false

        if (!familyName || !firstName || !moveOutDate ||
            !phoneNumberAfterMoveOut || !prefectureAfterMoveOut ||
            !address2AfterMoveOut || !address3AfterMoveOut
        ) {
            applyButtonDisabled = true
        }
        this.setState({
            applyButtonDisabled: applyButtonDisabled,
        })
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            familyName, firstName, moveOutDate, phoneNumberAfterMoveOut,
            zipCodeAfterMoveOut, prefectureAfterMoveOut,
            address2AfterMoveOut, address3AfterMoveOut,
            buildingRoomNumberAfterMoveOut, mailAddress, mailAddressConfirm,
        } = this.state

        const request: NatPostOldMemberRequest = {
            familyName: familyName,
            firstName: firstName,
            moveOutDate: moveOutDate,
            phoneNumberAfterMoveOut: phoneNumberAfterMoveOut,
            zipCodeAfterMoveOut: zipCodeAfterMoveOut,
            prefectureAfterMoveOut: prefectureAfterMoveOut,
            address2AfterMoveOut: address2AfterMoveOut,
            address3AfterMoveOut: address3AfterMoveOut,
            buildingRoomNumberAfterMoveOut: buildingRoomNumberAfterMoveOut,
            mailAddress: mailAddress,
            mailAddressConfirm: mailAddressConfirm,
            version: this.props.location.state.applyInfo?.version ?? null,
            confirmed: false,
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    onClickBackButton(): void {
        this.props.onClickBackButton()
    }

    async getAddressFromZipCode(): Promise<void> {
        const {zipCodeAfterMoveOut} = this.state
        const {condominiumRepo} = this.props

        await this.props.eventEmitter.showLoading()

        try {
            // 桁数不正の場合、単純に結果が取得できないので特に事前チェックは行わない
            const firstPart = zipCodeAfterMoveOut.substring(0, 3)
            const lastPart = zipCodeAfterMoveOut.substring(3, zipCodeAfterMoveOut.length)

            await condominiumRepo.getZipCode(firstPart, lastPart).then(
                zipData => {
                    const zipCodeResponse: NatZipCode = zipData
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data[0] && zipCodeResponse.data[0].ja) {
                        this.setState({
                            prefectureAfterMoveOut: zipCodeResponse.data[0].ja.prefecture,
                            address2AfterMoveOut: zipCodeResponse.data[0].ja.address1,
                            address3AfterMoveOut: zipCodeResponse.data[0].ja.address2,
                        }, () => {
                            this.updateApplyConfirmButtonDisabled()
                        })
                    }
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data.length > 1) {
                        const requestItemError = new RequestItemError(
                            'zipCodeAfterMoveOut', Messages.WCONA0002.toString(), 'WCONA0002')
                        this.props.eventEmitter.emit(requestItemError.resultCode, requestItemError)

                    }
                },
            ).catch(
                () => {
                    const requestItemError = new RequestItemError(
                        'zipCodeAfterMoveOut', Messages.ECONA0001.toString(), 'ECONA0001')
                    this.props.eventEmitter.emit(requestItemError.resultCode, requestItemError)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderTargetRoomInfo(): ReactElement {
        const applicantRoom = NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatGuestLoginInfoSessionStorageHelper
                .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_RIDGE_NAME) + ' ' +
            convertToRoomNumberForResident(
                NatGuestLoginInfoSessionStorageHelper
                    .getStringItemOrNull(GuestLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    対象住戸
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderOldMemberNameForm(): ReactElement {
        const {familyName, firstName} = this.state
        return (
            <>
                <ItemHeading>
                    旧組合員情報
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'familyName',
                                        maxLength: 10,
                                        value: familyName,
                                        onChange: this.handleChangeText,
                                        placeholder: '例：旧組合員',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['familyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'firstName',
                                        maxLength: 10,
                                        value: firstName,
                                        placeholder: '例：太郎',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['firstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'10px'}/>
                    <Box fontSize={FontSize.Middle}>※区分所有者の氏名を入力してください</Box>
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderAboutMoveOutInfoForm(): ReactElement {
        const {moveOutDate, holidays} = this.state

        return (
            <>
                <ItemHeading>
                    退去予定日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'退去予定日'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={moveOutDate}
                                onChange={this.handleChangeMoveOutDate}
                                holidays={holidays}
                                itemValue={''}
                                maxDate={new Date(moment(this.props.location.state.ownershipTransferDate).subtract(1, 'days').format('YYYY-MM-DD'))}
                                eventKeys={['moveOutDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderAfterMoveOutInfoForm(): ReactElement {
        const {
            phoneNumberAfterMoveOut,
            zipCodeAfterMoveOut,
            prefectureAfterMoveOut,
            address2AfterMoveOut,
            address3AfterMoveOut,
            buildingRoomNumberAfterMoveOut,
            mailAddress,
            mailAddressConfirm,
        } = this.state

        return (
            <>
                <ItemHeading>
                    退去後の連絡先
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={<span>電話番号<BreakPcOnly/>（ハイフン不要）</span>}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 11,
                                    name: 'phoneNumberAfterMoveOut',
                                    value: phoneNumberAfterMoveOut,
                                    placeholder: '例：08012345678',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['phoneNumberAfterMoveOut']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'郵便番号（ハイフン不要）'}
                        component={
                            <ErrorMessageAreaWrapper>
                                <FlexContainer>
                                    <Box flex={2} minWidth={0}>
                                        <PPTextFieldForResident
                                            inputProps={{
                                                maxLength: 7,
                                                name: 'zipCodeAfterMoveOut',
                                                value: zipCodeAfterMoveOut,
                                                placeholder: '例：1234567',
                                            }}
                                            onChange={this.handleChangeText}
                                            eventKeys={['zipCodeAfterMoveOut']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </Box>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <Box flex={1} maxWidth={'160px'} minWidth={'125px'} maxHeight={'50px'}>
                                        <DefaultStyledButton
                                            onClick={() => this.getAddressFromZipCode()}>
                                            住所自動入力
                                        </DefaultStyledButton>
                                    </Box>
                                </FlexContainer>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'都道府県'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 4,
                                    name: 'prefectureAfterMoveOut',
                                    value: prefectureAfterMoveOut,
                                    placeholder: '例：東京都',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['prefectureAfterMoveOut']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'市区町村'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'address2AfterMoveOut',
                                    value: address2AfterMoveOut,
                                    placeholder: '例：港区',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['address2AfterMoveOut']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'番地'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'address3AfterMoveOut',
                                    value: address3AfterMoveOut,
                                    placeholder: '例：芝４－６－６',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['address3AfterMoveOut']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'建物名・部屋番号'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'buildingRoomNumberAfterMoveOut',
                                    value: buildingRoomNumberAfterMoveOut,
                                    placeholder: '例：すてきマンション　１０１号室',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['buildingRoomNumberAfterMoveOut']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <Box color={Colors.Body}>メールアドレスをご入力いただくと、提出完了メールを受け取ることができます。</Box>
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'メールアドレス'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 100,
                                    name: 'mailAddress',
                                    value: mailAddress,
                                    placeholder: '例：mail@example.com',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['mailAddress']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatGuestOwnershipTransfersDetailFormItem
                        title={'メールアドレス（確認用）'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 100,
                                    name: 'mailAddressConfirm',
                                    value: mailAddressConfirm,
                                    placeholder: '例：mail@example.com',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['mailAddressConfirm']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderCaution(): ReactElement {
        return (
            <WideWhiteArea>
                <Box height={'20px'}/>
                <Box fontWeight={FontWeights.Bold}>【注意事項】</Box>
                <Box height={'10px'}/>
                <ListGridContainer fontSize={FontSize.Middle}>
                    <Box>・</Box>
                    <DisplayItemInputTitleNoMargin>
                        <Box component={'span'} color={Colors.Body}>
                            メールが届かない場合は、ご契約のプロバイダやメールソフトの設定などにより、受信ができない可能性がございます。
                        </Box>
                    </DisplayItemInputTitleNoMargin>
                    <Box>・</Box>
                    <DisplayItemInputTitleNoMargin>
                        <Box component={'span'} color={Colors.Body}>
                            迷惑メールの対策で、ドメイン受信設定を行っている場合は、「@suteki.haseko.jp」「@haseko.co.jp」を受信設定してください。
                            設定方法はご契約のプロバイダのWebサイトをご確認ください。
                        </Box>
                    </DisplayItemInputTitleNoMargin>
                    <Box>・</Box>
                    <DisplayItemInputTitleNoMargin>
                        <Box component={'span'} color={Colors.Body}>
                            yahooメール/gmail等のフリーメールアドレスでもご登録いただけます。スマートフォンをご利用のお客様でも、
                            SMSにて受信するメールアドレスをご利用の方は、確認メールが正しく表示されず、ご登録いただけませんので、ご注意ください。
                        </Box>
                    </DisplayItemInputTitleNoMargin>
                </ListGridContainer>
                <Box height={'20px'}/>
            </WideWhiteArea>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderTargetRoomInfo()}
                <Box height={'15px'}/>
                {this.renderOldMemberNameForm()}
                <Box height={'15px'}/>
                {this.renderAboutMoveOutInfoForm()}
                <Box height={'15px'}/>
                {this.renderAfterMoveOutInfoForm()}
                {this.renderCaution()}
                <Box height={'20px'}/>
                <GuestApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </GuestApplyButton>
                <Box height={'30px'}/>
                <GuestBackButton onClick={this.onClickBackButton}>
                    戻る
                </GuestBackButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
